// caiet UC31-02 / UC31-03: the secret is shown exactly once, and the copy button
// is the way out of the dialog. Confirming stays disabled until the copy button
// has been used, because the server cannot show the value again — it only ever
// held the SHA-256.
// per docs/shape-ops-credential-copy-and-secret-date.md §2.2
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudInlineMessage,
  MudModal,
  MudTextInput,
} from '@egov-moldova/mud-react';
import { onModalDismiss } from '@simsm/ui-shared';
import { copyText } from './clipboard';

// `failed` is never `copied`: a browser that refused the clipboard is told to the
// Administrator, who copies the value by hand from the read-only field.
type CopyState = 'idle' | 'copied' | 'failed';

export interface SecretRevealModalProps {
  secret: string | null;
  onClose: () => void;
}

export function SecretRevealModal({ secret, onClose }: SecretRevealModalProps): JSX.Element {
  const { t } = useTranslate();
  const [copyState, setCopyState] = useState<CopyState>('idle');

  async function copy(): Promise<void> {
    if (!secret) return;
    setCopyState((await copyText(secret)) ? 'copied' : 'failed');
  }

  function close(): void {
    setCopyState('idle');
    onClose();
  }

  const failed = copyState === 'failed';
  // A failure enables confirming too: the Administrator was told to copy by
  // hand, and the dialog must not hold the only copy of the secret behind a
  // button this browser will never enable.
  const canConfirm = copyState !== 'idle';

  return (
    <MudModal
      closeLabel={t('common.close')}
      open={secret !== null}
      size="md"
      titleText={t('credentials.secret.title')}
      // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
      // components emit `mudClose` bubbling+composed, so a descendant closing inside this
      // modal reaches this handler too; the guard answers only a real dismissal.
      onMudClose={onModalDismiss(close)}
    >
      <div className="simsm-syscred__modal-body">
        <MudInfoBox emphasis="strong" variant="warning">
          {t('credentials.secret.warning')}
        </MudInfoBox>
        <div className="simsm-syscred__field">
          <div className="simsm-syscred__field-row">
            <MudTextInput
              size="md"
              readonly
              label={t('credentials.secret.label')}
              value={secret ?? ''}
            />
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              iconOnly
              label={t('credentials.secret.copy')}
              onClick={() => void copy()}
            >
              <MudIcon slot="icon" name="copy" size={20} />
            </MudButton>
          </div>
          <MudInlineMessage
            className="simsm-syscred__hint-message"
            variant={failed ? 'error' : 'info'}
            size="small"
            hideIcon
            role={failed ? 'alert' : undefined}
          >
            {failed
              ? t('credentials.secret.copy_failed')
              : copyState === 'copied'
                ? t('credentials.secret.copied_hint')
                : t('credentials.secret.copy_hint')}
          </MudInlineMessage>
        </div>
      </div>
      <div slot="actions" className="simsm-syscred__modal-actions">
        <MudButton
          shape="circular"
          variant="primary"
          size="md"
          disabled={!canConfirm}
          onClick={close}
        >
          {t('credentials.secret.done')}
        </MudButton>
      </div>
    </MudModal>
  );
}
